import clsx from 'clsx'
import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {ILayout, useLayout} from '../../core'
import {SidebarMenu} from './sidebar-menu/SidebarMenu'
import {SidebarFooter} from './SidebarFooter'
import {SidebarLogo} from './SidebarLogo'
import { useAuth } from '../../../../app/modules/auth'

// <--- 2. IMPORT ScrollComponent
import {ScrollComponent} from '../../../assets/ts/components'

const Sidebar = () => {
  const {config} = useLayout()
  const { currentUser } = useAuth()
  const sidebarRef = useRef<HTMLDivElement>(null)
  const {pathname} = useLocation()

  // Hide sidebar entirely for agent users
  const isAgent = currentUser?.roles?.some((r: any) =>
    (r?.name || r?.roleName || r || '').toString().toLowerCase().includes('agent')
  ) ?? false

  useEffect(() => {
    if (isAgent) {
      // Remove all sidebar-related body attributes so content fills full width
      document.body.removeAttribute('data-kt-app-sidebar-enabled')
      document.body.removeAttribute('data-kt-app-sidebar-fixed')
      document.body.removeAttribute('data-kt-app-sidebar-push-header')
      document.body.removeAttribute('data-kt-app-sidebar-push-toolbar')
      document.body.removeAttribute('data-kt-app-sidebar-push-footer')
      document.body.removeAttribute('data-kt-app-sidebar-minimize')
      document.body.removeAttribute('data-kt-app-sidebar-hoverable')
      return
    }
    updateDOM(config)
  }, [config, isAgent])

  useEffect(() => {
    setTimeout(() => {
      ScrollComponent.reinitialization()
    }, 500)
  }, [pathname])

  if (!config.app?.sidebar?.display || isAgent) {
    return null
  }

  return (
    <>
      {(config.layoutType === 'dark-sidebar' || config.layoutType === 'light-sidebar') && (
        <div
          ref={sidebarRef}
          id='sc_app_sidebar'
          className={clsx('app-sidebar', config.app?.sidebar?.default?.class)}
        >
          {/* 1. LOGO */}
          <SidebarLogo sidebarRef={sidebarRef} />

          {/* 2. MENU WRAPPER */}
          {/* ID 'kt_app_sidebar_menu' added */}
          <div
            id='kt_app_sidebar_menu'
            className='app-sidebar-menu overflow-hidden flex-column-fluid'
          >
            <div
              id='kt_app_sidebar_menu_scroll'
              className='app-sidebar-wrapper hover-scroll-overlay-y my-5'
              // --- ATRIBUT SCROLL METRONIC ---
              data-kt-scroll='true'
              data-kt-scroll-activate='true'
              data-kt-scroll-height='auto'
              data-kt-scroll-dependencies='#kt_app_sidebar_logo, #kt_app_sidebar_footer'
              // Ini merujuk ke ID wrapper di atas (kt_app_sidebar_menu)
              data-kt-scroll-wrappers='#kt_app_sidebar_menu'
              data-kt-scroll-offset='5px'
              data-kt-scroll-save-state='true'
            >
              <SidebarMenu />
            </div>
          </div>

          {/* 3. FOOTER */}
          <SidebarFooter />
        </div>
      )}
    </>
  )
}

const updateDOM = (config: ILayout) => {
  if (config.layoutType === 'dark-sidebar' || config.layoutType === 'light-sidebar') {
    if (config.app?.sidebar?.default?.minimize?.desktop?.enabled) {
      if (config.app?.sidebar?.default?.minimize?.desktop?.default) {
        document.body.setAttribute('data-kt-app-sidebar-minimize', 'on')
      }

      if (config.app?.sidebar?.default?.minimize?.desktop?.hoverable) {
        document.body.setAttribute('data-kt-app-sidebar-hoverable', 'true')
      }
    }

    if (config.app?.sidebar?.default?.minimize?.mobile?.enabled) {
      if (config.app?.sidebar?.default?.minimize?.mobile?.default) {
        document.body.setAttribute('data-kt-app-sidebar-minimize-mobile', 'on')
      }

      if (config.app?.sidebar?.default?.minimize?.mobile?.hoverable) {
        document.body.setAttribute('data-kt-app-sidebar-hoverable-mobile', 'true')
      }
    }

    if (config.app?.sidebar?.default?.collapse?.desktop?.enabled) {
      if (config.app?.sidebar?.default?.collapse?.desktop?.default) {
        document.body.setAttribute('data-kt-app-sidebar-collapse', 'on')
      }
    }

    if (config.app?.sidebar?.default?.collapse?.mobile?.enabled) {
      if (config.app?.sidebar?.default?.collapse?.mobile?.default) {
        document.body.setAttribute('data-kt-app-sidebar-collapse-mobile', 'on')
      }
    }

    if (config.app?.sidebar?.default?.push) {
      if (config.app?.sidebar?.default?.push?.header) {
        document.body.setAttribute('data-kt-app-sidebar-push-header', 'true')
      }

      if (config.app?.sidebar?.default?.push?.toolbar) {
        document.body.setAttribute('data-kt-app-sidebar-push-toolbar', 'true')
      }

      if (config.app?.sidebar?.default?.push?.footer) {
        document.body.setAttribute('data-kt-app-sidebar-push-footer', 'true')
      }
    }

    if (config.app?.sidebar?.default?.stacked) {
      document.body.setAttribute('app-sidebar-stacked', 'true')
    }

    document.body.setAttribute('data-kt-app-sidebar-enabled', 'true')
    document.body.setAttribute(
      'data-kt-app-sidebar-fixed',
      config.app?.sidebar?.default?.fixed?.desktop?.toString() || ''
    )

    const appSidebarDefaultDrawerEnabled = config.app?.sidebar?.default?.drawer?.enabled
    let appSidebarDefaultDrawerAttributes: {[attrName: string]: string} = {}
    if (appSidebarDefaultDrawerEnabled) {
      appSidebarDefaultDrawerAttributes = config.app?.sidebar?.default?.drawer?.attributes as {
        [attrName: string]: string
      }
    }

    const appSidebarDefaultStickyEnabled = config.app?.sidebar?.default?.sticky?.enabled
    let appSidebarDefaultStickyAttributes: {[attrName: string]: string} = {}
    if (appSidebarDefaultStickyEnabled) {
      appSidebarDefaultStickyAttributes = config.app?.sidebar?.default?.sticky?.attributes as {
        [attrName: string]: string
      }
    }

    setTimeout(() => {
      const sidebarElement = document.getElementById('sc_app_sidebar')
      // sidebar
      if (sidebarElement) {
        const sidebarAttributes = sidebarElement
          .getAttributeNames()
          .filter((t) => t.indexOf('data-') > -1)
        sidebarAttributes.forEach((attr) => sidebarElement.removeAttribute(attr))

        if (appSidebarDefaultDrawerEnabled) {
          for (const key in appSidebarDefaultDrawerAttributes) {
            if (appSidebarDefaultDrawerAttributes.hasOwnProperty(key)) {
              sidebarElement.setAttribute(key, appSidebarDefaultDrawerAttributes[key])
            }
          }
        }

        if (appSidebarDefaultStickyEnabled) {
          for (const key in appSidebarDefaultStickyAttributes) {
            if (appSidebarDefaultStickyAttributes.hasOwnProperty(key)) {
              sidebarElement.setAttribute(key, appSidebarDefaultStickyAttributes[key])
            }
          }
        }
      }
    }, 0)
  }
}

export {Sidebar}
